import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RotateCcw, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button, Label, Textarea } from "@/design-system/components/ui";
import { brokersApi, isArchiveStatus, isPipelineStatus, type ArchiveTargetStatus, type BrokerStatus } from "@/api/brokers";

/**
 * Broker Channel Batch (WS5) — archive / reactivate control. Pipeline
 * progression moved to the header BrokerStageStepper + StageAdvancePanel;
 * this modal now only groups the remaining lifecycle exits and returns:
 *
 *   • Archive               (Inactive / Suspended / Declined / Disqualify)
 *   • Reactivate            (archive status → a pipeline status)
 *
 * Archive / reactivation options are grouped from GET /valid-transitions.
 * Process requirements preserved:
 *   - a reason is required for archive / disqualify;
 *   - Disqualify is only offered with the Brokers.Disqualify permission and
 *     routes through the dedicated endpoint;
 *   - reactivating a Disqualified broker shows the E1/E2 warning (the server
 *     enforces the permission).
 */
const ARCHIVE_TARGETS: ArchiveTargetStatus[] = ["Inactive", "Suspended", "Declined"];

type ActionKind = "archive" | "disqualify" | "reactivate";

export function StatusChangeModal({
  broker,
  canArchive,
  canDisqualify,
  onClose,
  onConfirmArchive,
  onConfirmDisqualify,
  onConfirmReactivate,
}: {
  broker: { id: string; companyName: string; status: BrokerStatus };
  /** Brokers.Update — gates archive + reactivate options. */
  canArchive: boolean;
  canDisqualify: boolean;
  onClose: () => void;
  onConfirmArchive: (target: ArchiveTargetStatus, reason: string) => Promise<void>;
  onConfirmDisqualify: (reason: string) => Promise<void>;
  onConfirmReactivate: (target: BrokerStatus, notes: string) => Promise<void>;
}) {
  const archived = isArchiveStatus(broker.status);
  const [target, setTarget] = useState<BrokerStatus | "">("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transitions = useQuery({
    queryKey: ["broker-transitions", broker.id, broker.status],
    queryFn: () => brokersApi.transitions(broker.id),
  });
  const allowed = useMemo(() => transitions.data ?? [], [transitions.data]);

  // In archive mode the pipeline statuses are the reactivation targets,
  // gated by Brokers.Update (canArchive); archive targets need the same.
  const reactivateTargets = archived && canArchive ? allowed.filter(isPipelineStatus) : [];
  const archiveTargets = canArchive
    ? allowed.filter((s): s is ArchiveTargetStatus => (ARCHIVE_TARGETS as BrokerStatus[]).includes(s))
    : [];
  const disqualifyAvailable = canDisqualify && allowed.includes("Disqualified");

  const kind: ActionKind | null = target === ""
    ? null
    : archived
      ? "reactivate"
      : target === "Disqualified"
        ? "disqualify"
        : "archive";

  const reasonRequired = kind === "archive" || kind === "disqualify";

  useEffect(() => { setError(null); }, [target]);

  const submit = async () => {
    if (kind === null) return;
    if (reasonRequired && !reason.trim()) {
      setError("A reason is required.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (kind === "archive") await onConfirmArchive(target as ArchiveTargetStatus, reason.trim());
      else if (kind === "disqualify") await onConfirmDisqualify(reason.trim());
      else await onConfirmReactivate(target as BrokerStatus, reason.trim());
      onClose();
    } catch (e) {
      setError((e as Error).message ?? "Status change failed.");
    } finally {
      setSaving(false);
    }
  };

  const fromDisqualified = broker.status === "Disqualified";

  return (
    <ModalShell
      title={archived ? "Reactivate broker" : "Archive broker"}
      subtitle={`${broker.companyName} — currently ${broker.status}`}
      onClose={onClose}
    >
      <div className="grid gap-space-3">
        {transitions.isLoading && (
          <div className="text-caption text-gray-mid">Loading available transitions…</div>
        )}

        {!archived && (archiveTargets.length > 0 || disqualifyAvailable) && (
          <div>
            <Label className="mb-space-1 block">Archive</Label>
            <div className="grid grid-cols-2 gap-space-2">
              {archiveTargets.map((s) => (
                <ActionRadio
                  key={s}
                  label={s === "Inactive" ? "Mark Inactive" : s === "Suspended" ? "Suspend" : "Decline"}
                  active={target === s}
                  onClick={() => setTarget(s)}
                />
              ))}
              {disqualifyAvailable && (
                <ActionRadio label="Disqualify" danger active={target === "Disqualified"} onClick={() => setTarget("Disqualified")} />
              )}
            </div>
            {kind === "disqualify" && (
              <div className="text-caption text-gray-mid mt-space-2">
                Disqualification is permanent; reactivation later requires E1 or E2.
              </div>
            )}
          </div>
        )}

        {archived && (
          <>
            {fromDisqualified && (
              <div className="border-l-[3px] border-status-error bg-status-error/10 p-space-3 rounded-md text-small">
                <div className="flex items-center gap-space-2 text-status-error font-display uppercase tracking-wide">
                  <AlertTriangle size={14} strokeWidth={1.5} aria-hidden /> Executive approval required
                </div>
                <p className="mt-space-1 text-gray-dark">
                  This broker was disqualified. Reactivation requires E1 or E2 and will be logged with your user ID and notes.
                </p>
              </div>
            )}
            <div>
              <Label className="mb-space-1 block">Reactivate to</Label>
              <div className="grid grid-cols-2 gap-space-2">
                {reactivateTargets.map((s) => (
                  <ActionRadio key={s} label={s} active={target === s} onClick={() => setTarget(s)} />
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <Label className="mb-space-1 block" required={reasonRequired}>
            {archived ? "Notes" : "Reason"}
          </Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={kind === "disqualify"
              ? "State the disqualifying facts — this is logged in the audit trail."
              : kind === "archive"
                ? "Short note explaining the archive decision."
                : "Optional note for the activity timeline."}
          />
        </div>

        {error && <div className="text-small text-status-error">{error}</div>}

        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant={kind === "disqualify" ? "destructive" : "primary"}
            size="sm"
            onClick={submit}
            disabled={saving || kind === null}
          >
            {archived && <RotateCcw size={14} strokeWidth={1.5} aria-hidden />}
            {saving ? "Saving…" : kind === "disqualify" ? "Disqualify" : archived ? "Reactivate" : "Archive"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function ActionRadio({
  label, active, onClick, danger,
}: { label: string; active: boolean; onClick: () => void; danger?: boolean }) {
  const base = "rounded-md border px-space-3 py-space-2 text-small text-left transition-colors";
  const cls = active
    ? (danger ? "border-status-error bg-status-error/10 text-status-error font-semibold"
              : "border-gold bg-gold/10 text-onyx font-semibold")
    : "border-border-light bg-white-pure text-gray-mid hover:border-gold-shadow";
  return (
    <button type="button" onClick={onClick} className={`${base} ${cls}`}>
      {label}
    </button>
  );
}

function ModalShell({
  title, subtitle, children, onClose,
}: { title: string; subtitle: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — ${subtitle}`}
      className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
    >
      <div
        className="bg-white-pure rounded-lg shadow-lg max-w-lg w-full p-space-5 border-t-[2px] border-t-gold"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-space-3">
          <div>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
              Broker
            </div>
            <h2 className="font-display font-bold text-h3 text-onyx m-0">{title}</h2>
            <div className="text-caption text-gray-mid mt-space-1">{subtitle}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-mid hover:text-onyx">
            <X size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
